import React from "react";
import { Box, Typography } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Listeners only on the handle icon, so row text stays selectable.
const DragHandle = ({ listeners }) => (
  <Box
    {...listeners}
    sx={{ display: "flex", alignItems: "center", cursor: "grab", color: "text.secondary" }}
  >
    <DragIndicatorIcon fontSize="small" />
  </Box>
);

const SortableRow = ({ id, label, bold }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1,
        mb: 1,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        backgroundColor: isDragging ? "rgba(0,49,88,0.06)" : "#fff",
      }}
      {...attributes}
    >
      <DragHandle listeners={listeners} />
      <Typography fontWeight={bold ? 600 : 400}>{label}</Typography>
    </Box>
  );
};

const useGroupDragEnd = (items, onReorder) => {
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);
    onReorder(arrayMove(items, oldIndex, newIndex).map((item) => item.id));
  };
  return handleDragEnd;
};

const ChildSortableGroup = ({ items, onReorder, sensors }) => {
  const handleDragEnd = useGroupDragEnd(items, onReorder);
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
        {items.map((item) => (
          <SortableRow key={item.id} id={item.id} label={item.label} />
        ))}
      </SortableContext>
    </DndContext>
  );
};

// One continuous indented tree (EduTech style); drag is scoped to siblings
// only — reparenting stays a Parent Menu edit, not a cross-group drag.
const MenuReorderList = ({ rootItems, childGroupsByParentId, onRootReorder, onChildReorder }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const handleRootDragEnd = useGroupDragEnd(rootItems, onRootReorder);

  return (
    <Box>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleRootDragEnd}
      >
        <SortableContext items={rootItems.map((item) => item.id)} strategy={verticalListSortingStrategy}>
          {rootItems.map((item) => {
            const children = childGroupsByParentId[item.id];
            return (
              <Box key={item.id}>
                <SortableRow id={item.id} label={item.label} bold />
                {children && children.length > 0 && (
                  <Box sx={{ pl: 4 }}>
                    <ChildSortableGroup
                      items={children}
                      onReorder={(orderedIds) => onChildReorder(item.id, orderedIds)}
                      sensors={sensors}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        </SortableContext>
      </DndContext>
    </Box>
  );
};

export default MenuReorderList;
